import { useMemo, useState } from 'react';
import { Box } from '@chakra-ui/react';
import { Colors, Icon, Text, Button } from '@mercator-gcp/grout';
import { Spinner } from 'components/elements/Spinner';
import Tooltip from 'components/elements/Tooltip';
import { FlexColumn } from 'components/layout/FlexColumn/FlexColumn';
import { FlexRow } from 'components/layout/FlexRow';
import { useCompanyProjectCityCounts } from 'hooks/useCompanies';
import { useAvailableCities } from 'hooks/useUserCityAccess';
import { formatInteger } from 'utils/format';

const tableHeaderStyles = {
  color: Colors.TextQuiet,
  marginBottom: 'padding_4',
};

const upgradeTextStyles = {
  color: Colors.Tangerine900,
  marginLeft: 'padding_4',
  cursor: 'pointer',
};

const disabledCityStyles = {
  color: Colors.TextQuiet,
};

const upgradeContainer = {
  gap: 'padding_4',
};

const flexRowStyles = {
  alignItems: 'center',
  justifyContent: 'space-between',
};

const tooltipText =
  'To access projects in additional markets, please contact support@mercator.ai';

interface CompanyProjectLocationsProps {
  id: string;
}

const INITIAL_DISPLAYED_CITIES_COUNT = 8;

export const CompanyProjectLocations = ({ id }: CompanyProjectLocationsProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const {
    data: companyProjectCityCounts,
    error,
    isLoading,
  } = useCompanyProjectCityCounts(id);
  const { availableCities } = useAvailableCities();

  // Company project city names CAN come in as `${city}` or `${city} ${state}`.
  const formattedAvailableCities = availableCities
    // build the possible list of company city names from the permission city name
    .map((city) => [
      city.name.toLowerCase(),
      `${city.name} ${city.state}`.toLocaleLowerCase(),
    ]);

  const companyCitiesWithCountAndPermission = useMemo(() => {
    return companyProjectCityCounts?.map((companyCityWithCount) => {
      /**
       * Company project city names CAN come in as `${city}` or `${city} ${state}`.
       * The available cities (i.e. the cities the user has permission for) is nicely broken up into { city: ... , state: ... }
       *
       * We have to do a more complicated match to _either_ city formatting.
       * 1. Go through each permissioned city and build a list with either formatting of the city name.
       * 2. Match for at least ONE of the two above configurations to the company city name.
       */
      const indexOfMatchedAvailableCity =
        // do we match at least one of the constructed city names?
        formattedAvailableCities.findIndex((availableCityOptions) =>
          availableCityOptions.includes(companyCityWithCount.city.toLowerCase()),
        );

      // `findIndex()` will return -1 if there is no match.
      const isCityAllowed = indexOfMatchedAvailableCity >= 0;

      /**
       * If we have a permission match, use the city name from the permissions since it is more nicely broken up and has a clean city name.
       * Otherwise, use what we got from the company city name.
       */
      const cityLabel = isCityAllowed
        ? availableCities[indexOfMatchedAvailableCity]?.name
        : companyCityWithCount.city;

      // Rebuild the payload to have all the necessary information for the UX
      return {
        isCityAllowed,
        city: cityLabel,
        count: companyCityWithCount.count,
      };
    });
  }, [availableCities, formattedAvailableCities, companyProjectCityCounts]);

  // Limit displayed locations to initial when not expanded
  const displayedCities = useMemo(() => {
    if (!companyCitiesWithCountAndPermission) return [];

    return isExpanded
      ? companyCitiesWithCountAndPermission
      : companyCitiesWithCountAndPermission.slice(0, INITIAL_DISPLAYED_CITIES_COUNT);
  }, [companyCitiesWithCountAndPermission, isExpanded]);

  const hasMoreLocations = useMemo(() => {
    return (
      companyCitiesWithCountAndPermission &&
      companyCitiesWithCountAndPermission.length > INITIAL_DISPLAYED_CITIES_COUNT
    );
  }, [companyCitiesWithCountAndPermission]);

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  if (isLoading) {
    return (
      <Spinner
        margin="auto"
        thickness="4px"
        speed="1.65s"
        gridColumn="1/3"
        emptyColor="gray.200"
        size="lg"
      />
    );
  }

  if (error) {
    return null;
  }

  return (
    <FlexColumn>
      <FlexRow sx={tableHeaderStyles}>
        <Text.SmallLabel>top locations</Text.SmallLabel>
        <Text.SmallLabel>projects</Text.SmallLabel>
      </FlexRow>
      <Box>
        {displayedCities?.map((cityInfo) => {
          return (
            <FlexRow
              key={`company-project-locations-container-${id}-${cityInfo.city}`}
              sx={{
                ...flexRowStyles,
                ...(cityInfo.isCityAllowed ? {} : disabledCityStyles),
              }} // Apply styles if city is not allowed
            >
              <FlexRow
                key={`company-project-locations-container-city-name-${id}-${cityInfo.city}`}
                sx={upgradeContainer}
              >
                <Text.LargeBold
                  key={`company-project-locations-city-name-${id}-${cityInfo.city}`}
                >
                  {cityInfo.city}
                </Text.LargeBold>
                <Tooltip
                  key={`company-project-locations-${id}-${cityInfo.city}`}
                  tooltipText={tooltipText}
                  isDisabled={cityInfo.isCityAllowed}
                  placement="top"
                >
                  {!cityInfo.isCityAllowed && (
                    <FlexRow
                      key={`company-project-locations-city-allowed-container-${id}-${cityInfo.city}`}
                    >
                      <Icon
                        key={`company-project-locations-city-allowed-icon-${id}-${cityInfo.city}`}
                        fill={Colors.Tangerine900}
                        name={Icon.Name.Upgrade}
                      />
                      <Text.SmallLabel
                        key={`company-project-locations-city-allowed-text-${id}-${cityInfo.city}`}
                        sx={upgradeTextStyles}
                      >
                        UPGRADE MARKETS
                      </Text.SmallLabel>
                    </FlexRow>
                  )}
                </Tooltip>
              </FlexRow>
              <Text
                key={`company-project-locations-count-${id}-${cityInfo.city}`}
                className="textBaseLight"
              >
                {formatInteger(cityInfo.count)}
              </Text>
            </FlexRow>
          );
        })}
        {hasMoreLocations && (
          <FlexRow sx={{ justifyContent: 'center', marginTop: 'padding_8' }}>
            <Button
              variant="link"
              onClick={toggleExpanded}
              endAdornment={
                <Icon name={isExpanded ? Icon.Name.ChevronUp : Icon.Name.ChevronDown} />
              }
            >
              {isExpanded ? 'View less' : 'View more'}
            </Button>
          </FlexRow>
        )}
      </Box>
    </FlexColumn>
  );
};
